import apiClient from "@/lib/api-client"
import type {
  Category,
  Author,
  Theme,
  Course,
  Lesson,
  Review,
  ReviewCreateRequest,
  ReviewUpdateRequest,
  PaginatedResponse,
} from "@/types/api"

const buildQueryString = (params?: Record<string, any>): string => {
  if (!params) return ""
  const query = new URLSearchParams(params as any).toString()
  return query ? `?${query}` : ""
}

export const courseService = {
  // Categories
  getCategories: async (params?: {
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<Category>> => {
    return apiClient.get<PaginatedResponse<Category>>(
      `/course/categories/${buildQueryString(params)}`
    )
  },

  getCategory: async (slug: string): Promise<Category> => {
    return apiClient.get<Category>(`/course/categories/${slug}/`)
  },

  createCategory: async (data: FormData): Promise<Category> => {
    return apiClient.post<Category>("/course/categories/", data)
  },

  updateCategory: async (slug: string, data: FormData): Promise<Category> => {
    return apiClient.patch<Category>(`/course/categories/${slug}/`, data)
  },

  deleteCategory: async (slug: string): Promise<void> => {
    await apiClient.delete(`/course/categories/${slug}/`)
  },

  // Authors
  getAuthors: async (params?: {
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<Author>> => {
    return apiClient.get<PaginatedResponse<Author>>(
      `/course/authors/${buildQueryString(params)}`
    )
  },

  getAuthor: async (slug: string): Promise<Author> => {
    return apiClient.get<Author>(`/course/authors/${slug}/`)
  },

  createAuthor: async (data: FormData): Promise<Author> => {
    return apiClient.post<Author>("/course/authors/", data)
  },

  updateAuthor: async (slug: string, data: FormData): Promise<Author> => {
    return apiClient.patch<Author>(`/course/authors/${slug}/`, data)
  },

  deleteAuthor: async (slug: string): Promise<void> => {
    await apiClient.delete(`/course/authors/${slug}/`)
  },

  // Themes
  getThemes: async (params?: {
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<Theme>> => {
    return apiClient.get<PaginatedResponse<Theme>>(
      `/course/themes/${buildQueryString(params)}`
    )
  },

  getTheme: async (slug: string): Promise<Theme> => {
    return apiClient.get<Theme>(`/course/themes/${slug}/`)
  },

  createTheme: async (data: { title?: string }): Promise<Theme> => {
    return apiClient.post<Theme>("/course/themes/", data)
  },

  updateTheme: async (slug: string, data: { title?: string }): Promise<Theme> => {
    return apiClient.patch<Theme>(`/course/themes/${slug}/`, data)
  },

  deleteTheme: async (slug: string): Promise<void> => {
    await apiClient.delete(`/course/themes/${slug}/`)
  },

  // Courses
  getCourses: async (params?: {
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<Course>> => {
    return apiClient.get<PaginatedResponse<Course>>(
      `/course/courses/${buildQueryString(params)}`
    )
  },

  getCourse: async (slug: string): Promise<Course> => {
    return apiClient.get<Course>(`/course/courses/${slug}/`)
  },

  createCourse: async (data: FormData): Promise<Course> => {
    return apiClient.post<Course>("/course/courses/", data)
  },

  updateCourse: async (slug: string, data: FormData): Promise<Course> => {
    return apiClient.patch<Course>(`/course/courses/${slug}/`, data)
  },

  deleteCourse: async (slug: string): Promise<void> => {
    await apiClient.delete(`/course/courses/${slug}/`)
  },

  // Lessons
  getLessons: async (params?: {
    course_slug?: string
    limit?: number
    offset?: number
  }): Promise<PaginatedResponse<Lesson>> => {
    return apiClient.get<PaginatedResponse<Lesson>>(
      `/course/lessons/${buildQueryString(params)}`
    )
  },

  getLesson: async (id: number): Promise<Lesson> => {
    return apiClient.get<Lesson>(`/course/lessons/${id}/`)
  },

  createLesson: async (data: {
    title: string
    description: string
    course: number
    video_id?: number
    order?: number
    is_active?: boolean
  }): Promise<Lesson> => {
    return apiClient.post<Lesson>("/course/lessons/", data)
  },

  updateLesson: async (
    id: number,
    data: Partial<{
      title: string
      description: string
      video_id: number | null
      order: number
      is_active: boolean
    }>
  ): Promise<Lesson> => {
    return apiClient.patch<Lesson>(`/course/lessons/${id}/`, data)
  },

  deleteLesson: async (id: number): Promise<void> => {
    await apiClient.delete(`/course/lessons/${id}/`)
  },

  // Reviews
  getReviews: async (params?: {
    limit?: number
    offset?: number
    course_slug?: string
    is_featured?: boolean
    type?: "general" | "course"
  }): Promise<PaginatedResponse<Review>> => {
    return apiClient.get<PaginatedResponse<Review>>(
      `/course/reviews/${buildQueryString(params)}`
    )
  },

  getReview: async (id: number): Promise<Review> => {
    return apiClient.get<Review>(`/course/reviews/${id}/`)
  },

  createReview: async (data: ReviewCreateRequest): Promise<Review> => {
    return apiClient.post<Review>("/course/reviews/", data)
  },

  // Create anonymous review (without authentication)
  createAnonymousReview: async (data: ReviewCreateRequest): Promise<Review> => {
    const API_BASE_URL = "https://api.skylls.uz/api/v1"
    const response = await fetch(`${API_BASE_URL}/course/reviews/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw {
        status: response.status,
        message: response.statusText,
        data: errorData,
      }
    }

    return await response.json()
  },

  updateReview: async (id: number, data: ReviewUpdateRequest): Promise<Review> => {
    return apiClient.patch<Review>(`/course/reviews/${id}/`, data)
  },

  deleteReview: async (id: number): Promise<void> => {
    await apiClient.delete(`/course/reviews/${id}/`)
  },

  approveReview: async (id: number): Promise<Review> => {
    return apiClient.post<Review>(`/course/reviews/${id}/approve/`)
  },

  unapproveReview: async (id: number): Promise<Review> => {
    return apiClient.post<Review>(`/course/reviews/${id}/unapprove/`)
  },

  featureReview: async (id: number): Promise<Review> => {
    return apiClient.post<Review>(`/course/reviews/${id}/feature/`)
  },

  unfeatureReview: async (id: number): Promise<Review> => {
    return apiClient.post<Review>(`/course/reviews/${id}/unfeature/`)
  },
}
